import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/axios';
import { Country } from 'country-state-city';
import { getCurrencyCodeByCountry } from '../utils/currency';

interface CurrencyContextType {
    currency: string;
    symbol: string;
    formatPrice: (amount: number, options?: { hideSymbol?: boolean, showCode?: boolean, showDecimals?: boolean, skipConversion?: boolean }) => string;
    exchangeRates: Record<string, number>;
    convertToBase: (amount: number) => number;
    convertFromBase: (amount: number) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

/** Convert a country name or ISO2/ISO3 code to an ISO 4217 currency code */
const getCurrencyFromCountry = (countryName: string): string | null => {
    if (!countryName) return null;
    
    // Use first our local utility which handles special regions like Europe
    const localMatch = getCurrencyCodeByCountry(countryName);
    if (localMatch && localMatch !== 'USD') return localMatch;

    // Fallback to strict country-state-city matching
    const match = Country.getAllCountries().find(c =>
        c.name.toLowerCase() === countryName.toLowerCase() ||
        c.isoCode.toLowerCase() === countryName.toLowerCase()
    );
    return match?.currency || localMatch || null;
};

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const { i18n } = useTranslation();
    const prevUserIdRef = useRef<string | undefined>(undefined);

    const [currency, setCurrency] = useState(() => localStorage.getItem('user-currency') || 'INR');
    const [exchangeRates, setExchangeRates] = useState<Record<string, number>>(() => {
        const cached = localStorage.getItem('exchange-rates');
        return cached ? JSON.parse(cached) : {
            INR: 1,
            USD: 0.012,
            GBP: 0.0094,
            EUR: 0.011,
            AED: 0.044,
            JPY: 1.83,
            AUD: 0.018,
            CAD: 0.016,
            SGD: 0.016,
            ZAR: 0.22
        };
    });

    // 1. Fetch Real-time Exchange Rates (base: INR)
    useEffect(() => {
        const fetchRates = async () => {
            try {
                const response = await fetch('https://api.exchangerate-api.com/v4/latest/INR');
                const data = await response.json();
                if (data.rates) {
                    setExchangeRates(data.rates);
                    localStorage.setItem('exchange-rates', JSON.stringify(data.rates));
                }
            } catch {
                console.warn('Primary exchange rate API failed, using cached/defaults.');
            }
        };
        fetchRates();
    }, []);

    // 2. Detect and Apply Local Currency
    // Only runs when user identity or role changes — NOT on every currency state change.
    useEffect(() => {
        const currentUserId = user?.id;

        // Always re-detect currency from profile when user is present
        // This prevents stale localStorage values from overriding the correct regional currency
        if (currentUserId) {
            localStorage.removeItem('user-currency');
        } else if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== currentUserId) {
            // User logged out — clear cache
            localStorage.removeItem('user-currency');
        }
        prevUserIdRef.current = currentUserId;

        const detectCurrency = async () => {
            try {
                let detectedCode: string | null = null;

                if (user) {
                    // Priority A: Detect from user's profile country stored in DB
                    let endpoint = '';
                    if (user.role === 'OWNER') endpoint = '/owners/me';
                    else if (user.role === 'DAYCARE') endpoint = '/daycare/profile';
                    else if (user.role === 'DOCTOR') endpoint = '/doctor/me';
                    else if (user.role === 'HOSPITAL') endpoint = '/doctor/me'; // doctor/me returns country for HOSPITAL users too
                    else if (user.role === 'GROOMING_AGENCY') endpoint = '/grooming-agency/me';
                    else if (user.role === 'TRAINER') endpoint = '/trainer/profile';
                    else if (user.role === 'PARTNER') endpoint = '/partners/profile';
                    else if (user.role === 'ECOMMERCE_AGENT' || user.role === 'PET_SHOP' || user.role === 'CONSULTATION') endpoint = '/ecommerce/me';
                    else if (user.role === 'INSURANCE') endpoint = '/insurance/me';

                    if (endpoint) {
                        try {
                            const { data } = await api.get(endpoint);

                            // FORCED OVERRIDE: If this is the Default Partner, ALWAYS use INR.
                            // This prevents "mixing" of currencies from multiple regions.
                            if (data.name === 'Default Partner') {
                                setCurrency('INR');
                                localStorage.setItem('user-currency', 'INR');
                                return;
                            }

                            // Most roles return data.country directly.
                            // PARTNER returns data.region?.country or data.location (combined string).
                            // Priority: ISO currencyCode field > 3-char currency field
                            if (data.currencyCode && data.currencyCode.length === 3) {
                                detectedCode = data.currencyCode;
                            } else if (data.currency && data.currency.length === 3) {
                                detectedCode = data.currency;
                            } else if (data.user?.currency && data.user?.currency.length === 3) {
                                detectedCode = data.user.currency;
                            }

                            let countryName: string | null =
                                data.country ||
                                data.facility?.country ||
                                data.hospital?.country ||
                                data.region?.country ||
                                (data.partner ? (data.partner.region?.country || data.partner.regions?.[0]?.country) : null) ||
                                (data.practice ? (data.practice.country || data.practice.region?.country) : null) ||
                                null;

                            // Fallback: parse combined location string e.g. "Bangalore, Karnataka, India"
                            if (!countryName && data.location && typeof data.location === 'string') {
                                const parts = data.location.split(',').map((p: string) => p.trim());
                                const last = parts[parts.length - 1];
                                if (last && last.toLowerCase() !== 'global') {
                                    countryName = last;
                                }
                            }

                            // Only override detectedCode from country name if we didn't already get a good ISO code
                            if (countryName && !detectedCode) {
                                detectedCode = getCurrencyFromCountry(countryName);
                            } else if (countryName && detectedCode === 'USD') {
                                // If we got USD as default fallback, try country name for a better match
                                const fromCountry = getCurrencyFromCountry(countryName);
                                if (fromCountry && fromCountry !== 'USD') {
                                    detectedCode = fromCountry;
                                }
                            }
                        } catch (profileErr) {
                            console.warn('Profile currency detection failed:', profileErr);
                        }
                    }
                }

                // Priority B: IP-based detection (for logged-out users OR when profile had no country)
                if (!detectedCode) {
                    const cached = localStorage.getItem('user-currency');
                    // If not logged in and we already have a cached value, keep it
                    if (!user && cached) return;

                    try {
                        // Primary: geojs.io (same service used in location.ts)
                        const ipRes = await fetch('https://get.geojs.io/v1/ip/geo.json');
                        if (ipRes.ok) {
                            const ipData = await ipRes.json();
                            if (ipData.country) {
                                detectedCode = getCurrencyFromCountry(ipData.country);
                            }
                        }
                    } catch {
                        // Second fallback: ipapi.co
                        try {
                            const ipRes2 = await fetch('https://ipapi.co/json/');
                            if (ipRes2.ok) {
                                const ipData2 = await ipRes2.json();
                                if (ipData2.currency) detectedCode = ipData2.currency;
                            }
                        } catch { /* give up, keep default */ }
                    }
                }

                if (detectedCode) {
                    if (detectedCode !== currency) {
                        setCurrency(detectedCode);
                    }
                    localStorage.setItem('user-currency', detectedCode);
                }
            } catch (error) {
                console.error('Currency detection system encountered an error:', error);
            }
        };

        detectCurrency();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.role]); // Do NOT include `currency` — that caused an infinite re-detection loop

    // 3. Dynamic Price Formatting & Conversion
    const formatPrice = useCallback((amount: number, options?: { hideSymbol?: boolean, showCode?: boolean, showDecimals?: boolean, skipConversion?: boolean }) => {
        const rate = options?.skipConversion ? 1 : (exchangeRates[currency] || 1);
        const convertedValue = (amount || 0) * rate;

        try {
            const formatter = new Intl.NumberFormat(i18n.language || (currency === 'INR' ? 'en-IN' : 'en-US'), {
                style: options?.hideSymbol ? 'decimal' : 'currency',
                currency: currency,
                minimumFractionDigits: options?.showDecimals === true ? 2 : (options?.showDecimals === false ? 0 : undefined),
                maximumFractionDigits: options?.showDecimals === true ? 2 : (options?.showDecimals === false ? 0 : undefined)
            });

            let formatted = formatter.format(convertedValue);

            if (options?.showCode) {
                formatted = `${formatted} ${currency}`;
            }

            return formatted;
        } catch {
            // Fallback if Intl doesn't recognise the currency code
            return `${currency} ${convertedValue.toFixed(2)}`;
        }
    }, [currency, exchangeRates]);

    const getSymbol = () => {
        try {
            return (0).toLocaleString(i18n.language || (currency === 'INR' ? 'en-IN' : 'en-US'), {
                style: 'currency',
                currency: currency
            }).replace(/\d|\s|[,.]/g, '');
        } catch {
            return currency === 'INR' ? '₹' : '$';
        }
    };

    return (
        <CurrencyContext.Provider value={{
            currency,
            symbol: getSymbol(),
            formatPrice,
            exchangeRates,
            convertToBase: (amount: number) => {
                const rate = exchangeRates[currency] || 1;
                // Use high precision for base storage to prevent rounding drift
                return Number((amount / rate).toFixed(6));
            },
            convertFromBase: (amount: number) => {
                const rate = exchangeRates[currency] || 1;
                return Number((amount * rate).toFixed(6));
            }
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};
